import { useEffect, useState } from 'react'
import { useAuth } from '@/lib/auth-context'

export type ParentUpdate = {
  id: string
  created_at: string
  kind: string | null
  note: string | null
}

export type StudentParentUpdates = {
  student_id: string
  student_name: string
  classroom_id: string
  updates: ParentUpdate[]
}

type State = {
  data: StudentParentUpdates[] | null
  loading: boolean
  error: string | null
}

export function useTeacherParentUpdates() {
  const { session } = useAuth()
  const accessToken = session?.access_token
  const [state, setState] = useState<State>({ data: null, loading: true, error: null })

  useEffect(() => {
    let cancelled = false

    async function load() {
      if (!accessToken) {
        if (!cancelled) {
          setState({ data: [], loading: false, error: 'Not logged in' })
        }
        return
      }

      try {
        setState((prev) => ({ ...prev, loading: true, error: null }))

        const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
        const res = await fetch(`${CORE_API}/api/v1/teacher/parent-updates`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })

        if (!res.ok) {
          const text = await res.text()
          throw new Error(text || `Request failed: ${res.status}`)
        }

        const raw = await res.json()
        // Unwrap Render { data, error } envelope
        const json = (raw?.data ?? raw) as StudentParentUpdates[]
        if (!cancelled) {
          setState({ data: json, loading: false, error: null })
        }
      } catch (err) {
        console.error('useTeacherParentUpdates error', err)
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to load parent updates',
          }))
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [accessToken])

  return state
}
